import { c as _c } from "react/compiler-runtime";
// Действия покупателя с заказом (fd-order-03): «Отменить заказ» — с
// подтверждением, необязательной причиной и тем, куда вернутся деньги;
// «Подтвердить получение» доставленного заказа. Отдельного кадра в макете нет —
// окно собрано из модалки кабинета (CabinetModal), как «Открыть спор».
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { api, type Order } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store";
import { canBuyerCancel, canBuyerConfirmReceipt, refundNotes } from "./order-cancel";
import { orderNumber } from "./order-view";
import { CabinetModal } from "./return-parts";
type Dialog = "cancel" | "receive" | null;
const errorText = (e: unknown, fallback: string) => e instanceof Error && e.message ? e.message : fallback;
export function OrderActions(t0) {
  const $ = _c(80);
  const {
    order,
    className
  } = t0;
  const fmt = useFormatPrice();
  const qc = useQueryClient();
  const userId = useProfile(_temp);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [result, setResult] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["payments", "config"],
      queryFn: _temp2,
      staleTime: 300000
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const payConfig = useQuery(t1);
  const autoRefund = payConfig.data?.refunds === true;
  let t2;
  if ($[1] !== order || $[2] !== qc || $[3] !== userId) {
    t2 = next => {
      qc.setQueryData(["buyer", userId, "orders", order.id], next);
      qc.invalidateQueries({
        queryKey: ["buyer", userId, "orders"],
        exact: true
      });
    };
    $[1] = order;
    $[2] = qc;
    $[3] = userId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const refresh = t2;
  let t3;
  if ($[5] !== order || $[6] !== reason) {
    t3 = () => api.buyer.cancelOrder(order.id, reason.trim() || undefined);
    $[5] = order;
    $[6] = reason;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== autoRefund || $[9] !== fmt || $[10] !== order || $[11] !== qc || $[12] !== userId) {
    t4 = next_0 => {
      qc.setQueryData(["buyer", userId, "orders", order.id], next_0);
      setResult(refundNotes(next_0, fmt, {
        autoRefund
      }));
      toast.success(`Заказ №${orderNumber(order.id, order.number)} отменён`);
    };
    $[8] = autoRefund;
    $[9] = fmt;
    $[10] = order;
    $[11] = qc;
    $[12] = userId;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: _temp3
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const cancel = useMutation(t5);
  let t6;
  if ($[17] !== order) {
    t6 = () => api.buyer.completeOrder(order.id);
    $[17] = order;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== refresh) {
    t7 = next_1 => {
      refresh(next_1);
      setDialog(null);
      toast.success("\u0421\u043F\u0430\u0441\u0438\u0431\u043E! \u041F\u043E\u043B\u0443\u0447\u0435\u043D\u0438\u0435 \u0437\u0430\u043A\u0430\u0437\u0430 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u043E");
    };
    $[19] = refresh;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = {
      mutationFn: t6,
      onSuccess: t7,
      onError: _temp4
    };
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const receive = useMutation(t8);
  let t9;
  if ($[24] !== order) {
    t9 = canBuyerCancel(order);
    $[24] = order;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const canCancel = t9;
  let t10;
  if ($[26] !== order) {
    t10 = canBuyerConfirmReceipt(order);
    $[26] = order;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const canReceive = t10;
  if (!canCancel && !canReceive && !dialog) {
    return null;
  }
  let t11;
  if ($[28] !== qc || $[29] !== result || $[30] !== userId) {
    t11 = open => {
      if (open) {
        return;
      }
      if (result) {
        qc.invalidateQueries({
          queryKey: ["buyer", userId, "orders"],
          exact: true
        });
      }
      setDialog(null);
      setResult(null);
      setReason("");
    };
    $[28] = qc;
    $[29] = result;
    $[30] = userId;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const close = t11;
  const stop = _temp5;
  let t12;
  if ($[32] !== className) {
    t12 = cn("flex flex-wrap gap-2", className);
    $[32] = className;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== canReceive) {
    t13 = canReceive && <Button size="sm" data-testid="order-confirm-receipt" onClick={() => setDialog("receive")}>Подтвердить получение</Button>;
    $[34] = canReceive;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== canCancel) {
    t14 = canCancel && <Button variant="gray" size="sm" data-testid="order-cancel" onClick={() => setDialog("cancel")}>Отменить заказ</Button>;
    $[36] = canCancel;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const t15 = dialog === "cancel";
  const t16 = result ? "\u0417\u0430\u043A\u0430\u0437 \u043E\u0442\u043C\u0435\u043D\u0451\u043D" : "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0437\u0430\u043A\u0430\u0437?";
  let t17;
  if ($[38] !== autoRefund || $[39] !== cancel || $[40] !== close || $[41] !== fmt || $[42] !== order || $[43] !== reason || $[44] !== result) {
    t17 = result ? <div className="flex flex-col gap-4" data-testid="order-cancel-result"><ul className="flex flex-col gap-2 text-[14px] leading-[18px] text-ds-primary">{result.map(_temp6)}</ul><Button block={true} onClick={() => close(false)}>Понятно</Button></div> : <div className="flex flex-col"><p className="text-[14px] leading-[18px] text-ds-primary">Заказ №{orderNumber(order.id, order.number)} на {fmt(order.total)} будет отменён, товары вернутся в продажу.</p><ul className="mt-3 flex flex-col gap-1.5 rounded-[12px] bg-[#F8F8F8] p-3 text-[14px] leading-[18px] text-ds-primary">{refundNotes(order, fmt, {
          autoRefund
        }).map(_temp7)}</ul><label className="mt-4 text-[16px] leading-5 font-semibold tracking-[-0.31px] text-ds-primary" htmlFor={`cancel-reason-${order.id}`}>Причина отмены <span className="font-normal text-black/50">(необязательно)</span></label><Textarea id={`cancel-reason-${order.id}`} value={reason} onChange={e_2 => setReason(e_2.target.value)} maxLength={500} rows={3} placeholder={"\u041D\u0430\u043F\u0440\u0438\u043C\u0435\u0440, \u043F\u0435\u0440\u0435\u0434\u0443\u043C\u0430\u043B \u0438\u043B\u0438 \u043E\u0444\u043E\u0440\u043C\u0438\u043B \u043F\u043E \u043E\u0448\u0438\u0431\u043A\u0435"} className="mt-2 min-h-[88px] resize-none" /><div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse"><Button className="sm:flex-1" data-testid="order-cancel-confirm" disabled={cancel.isPending} onClick={() => cancel.mutate()}>{cancel.isPending ? "\u041E\u0442\u043C\u0435\u043D\u044F\u0435\u043C\u2026" : "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0437\u0430\u043A\u0430\u0437"}</Button><Button variant="gray" className="sm:flex-1" onClick={() => close(false)}>Не отменять</Button></div></div>;
    $[38] = autoRefund;
    $[39] = cancel;
    $[40] = close;
    $[41] = fmt;
    $[42] = order;
    $[43] = reason;
    $[44] = result;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== close || $[47] !== t15 || $[48] !== t16 || $[49] !== t17) {
    t18 = <CabinetModal open={t15} onOpenChange={close} title={t16}>{t17}</CabinetModal>;
    $[46] = close;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const t19 = dialog === "receive";
  let t20;
  if ($[51] !== order.id || $[52] !== order.number) {
    t20 = orderNumber(order.id, order.number);
    $[51] = order.id;
    $[52] = order.number;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t20) {
    t21 = <p className="text-[14px] leading-[18px] text-ds-primary">Подтвердите, что получили заказ №{t20}. Он перейдёт в завершённые; оформить возврат по-прежнему можно в разделе «Возвраты».</p>;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== receive) {
    t22 = () => receive.mutate();
    $[56] = receive;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  const t23 = receive.isPending ? "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0430\u0435\u043C\u2026" : "\u0414\u0430, \u043F\u043E\u043B\u0443\u0447\u0438\u043B";
  let t24;
  if ($[58] !== receive.isPending || $[59] !== t22 || $[60] !== t23) {
    t24 = <Button className="sm:flex-1" data-testid="order-confirm-receipt-confirm" disabled={receive.isPending} onClick={t22}>{t23}</Button>;
    $[58] = receive.isPending;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== close) {
    t25 = <Button variant="gray" className="sm:flex-1" onClick={() => close(false)}>Отмена</Button>;
    $[62] = close;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t24 || $[65] !== t25) {
    t26 = <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">{t24}{t25}</div>;
    $[64] = t24;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== t21 || $[68] !== t26) {
    t27 = <div className="flex flex-col">{t21}{t26}</div>;
    $[67] = t21;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== close || $[71] !== t19 || $[72] !== t27) {
    t28 = <CabinetModal open={t19} onOpenChange={close} title={"\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u0438\u0435?"}>{t27}</CabinetModal>;
    $[70] = close;
    $[71] = t19;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] !== t12 || $[75] !== t13 || $[76] !== t14 || $[77] !== t18 || $[78] !== t28) {
    t29 = <div className={t12} onClick={stop} onKeyDown={stop}>{t13}{t14}{t18}{t28}</div>;
    $[74] = t12;
    $[75] = t13;
    $[76] = t14;
    $[77] = t18;
    $[78] = t28;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  return t29;
}
function _temp7(line_0) {
  return <li key={line_0}>{line_0}</li>;
}
function _temp6(line) {
  return <li key={line}>{line}</li>;
}
function _temp5(e_1) {
  return e_1.stopPropagation();
}
function _temp4(e_0) {
  return toast.error(errorText(e_0, "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u0438\u0435"));
}
function _temp3(e) {
  return toast.error(errorText(e, "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0437\u0430\u043A\u0430\u0437"));
}
function _temp2() {
  return api.payments.config();
}
function _temp(s) {
  return s.user?.id ?? "guest";
}